import {
  Button,
  FieldWrapper,
  FlexBox,
  FormField,
  Text,
} from '@dixa/lib-shared-habla';
import {
  OAuthSettings,
  PartnerHookFormBasicAuth,
} from 'lib-integrations-partner-support';
import { config } from 'lib-shared-backend-config';
import { HookFormText } from 'lib-shared-hook-form';
import { useFormContext } from 'react-hook-form';

import { PartnerSettingsConfigurationSchema } from 'apis/partnerIntegrations';

type ThirdPartySettingsConfigurationSchemaFieldProps = {
  field: PartnerSettingsConfigurationSchema;
  isCreateMode: boolean;
  integrationType: string;
  /** The id of the integration, only available once it has been created. */
  integrationId?: string;
};

/**
 * A component for rendering a schema field input depending on the field type.
 *
 * @param field - The field to render.
 * @param isCreateMode - Whether the field is being created or updated.
 * @param integrationType - The type of the integration.
 * @param integrationId - The id of the integration, needed to authorize it.
 * @returns An input field depending on the field type.
 */
export const ThirdPartySettingsConfigurationSchemaField = ({
  field,
  isCreateMode,
  integrationType,
  integrationId,
}: ThirdPartySettingsConfigurationSchemaFieldProps): JSX.Element | null => {
  const form = useFormContext();
  const formField = form.watch(field.key);

  const {
    label,
    key,
    type,
    prefix,
    required,
    readOnly,
    placeholder,
    secret: isSecret,
  } = field;

  switch (type) {
    // A basic text input field.
    // Secret values never come back to the frontend. In case of a secret value
    // we show a button that enables the user to update the value. Otherwise we
    // will ignore the input field in the form.
    case 'input':
      return (
        <FormField>
          {formField?.isEditing || isCreateMode ? (
            <HookFormText
              label={label}
              name={`${key}.value`}
              rules={{
                required: required ? `${label} is required` : undefined,
              }}
              componentProps={{
                placeholder: placeholder ?? '',
                readOnly: readOnly ?? false,
                ...(prefix && {
                  prefix: {
                    type: 'custom',
                    value: prefix,
                  },
                }),
                ...(isSecret &&
                  !isCreateMode && {
                    prefix: {
                      type: 'button',
                      button: {
                        icon: 'close',
                        onClick: () => form.setValue(`${key}.isEditing`, false),
                      },
                    },
                  }),
              }}
            />
          ) : (
            <FieldWrapper label={label}>
              <Button
                variant="secondary"
                icon="pen"
                onClick={() => form.setValue(`${key}.isEditing`, true)}
              >
                Update {label}
              </Button>
            </FieldWrapper>
          )}
        </FormField>
      );
    // A pair of inputs (token + secret) combined into a single base64-encoded
    // `token:secret` value. The labels for each input come from the schema's
    // additionalProperties. The value is stored under `${key}.value` so it lands
    // in the same slot the submit handler reads, mirroring the `input` case.
    case 'basicAuthInput': {
      const { username, password } = (field.additionalProperties ?? {}) as {
        username?: { label: string };
        password?: { label: string };
      };
      const usernameLabel = username?.label ?? 'Username';
      const passwordLabel = password?.label ?? 'Password';

      return (
        <FormField>
          <PartnerHookFormBasicAuth
            name={`${key}.value`}
            label={label}
            control={form.control}
            isCreationMode={isCreateMode}
            rules={{
              required: required ? `${label} is required` : undefined,
            }}
            usernameLabel={usernameLabel}
            passwordLabel={passwordLabel}
            usernamePlaceholder={`Insert your ${usernameLabel.toLowerCase()}`}
            passwordPlaceholder={`Insert your ${passwordLabel.toLowerCase()}`}
          />
        </FormField>
      );
    }
    // The button that starts the OAuth authorization of the integration. It
    // can only be rendered once the integration exists.
    case 'oauth':
      return integrationId ? (
        <OAuthSettings
          configuration={{ baseUrl: config.url.partnerIntegrations }}
          integrationId={integrationId}
          integrationType={integrationType}
          label={label}
        />
      ) : null;
    // Only relevant when creating the integration, which is handled by the
    // modal instead of the form.
    case 'marketplaceApp':
      return null;
    default:
      return (
        <FormField>
          <FlexBox>
            <Text.Paragraph>Unsupported input type: {type}</Text.Paragraph>
          </FlexBox>
        </FormField>
      );
  }
};
